/**
 * @file Register.tsx
 * @description Merchant registration page. Split-layout form collecting business name, email,
 * and password. Calls the send-welcome-email edge function to handle signup and send a
 * verification email. Crypto wallet setup happens later in the dashboard via CryptoWalletSetup.
 *
 * How it works:
 * - Validates password match and minimum length client-side
 * - Calls the send-welcome-email edge function which creates the Supabase auth user
 *   and merchant record, then sends a welcome/verification email
 * - On success, shows a "check your email" confirmation screen
 * - Left panel (desktop only) shows marketing copy and feature highlights
 *
 * @changelog
 * - 2026-03-19: Added documentation header
 * - 2026-05-14: Removed Polygon wallet field; Solana wallet setup moved into the dashboard.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Zap, Globe, CheckCircle } from 'lucide-react';

const FEATURES = [
  { icon: Shield, text: 'Zero chargebacks, ever' },
  { icon: Zap, text: 'Instant USDC settlement' },
  { icon: Globe, text: '170+ countries supported' },
];

export default function Register() {
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-welcome-email`;
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          business_name: businessName,
          redirect_to: `${window.location.origin}/dashboard`,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.details || data.error || 'Registration failed');
      }

      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-navy-950 flex items-center justify-center px-6">
        <div className="w-full max-w-md text-center">
          <Link to="/" className="font-mono text-bone-white tracking-widest text-sm font-medium uppercase">
            BONESPAY
          </Link>
          <div className="mt-10 border border-navy-700/40 rounded-subtle bg-navy-900/30 p-8 md:p-10">
            <div className="w-14 h-14 bg-navy-800/50 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-7 h-7 text-bone-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
            </div>
            <h2 className="font-serif text-bone-white text-2xl mb-3">
              Check your email
            </h2>
            <p className="font-sans text-navy-200/70 text-sm leading-relaxed mb-6">
              We sent a verification link to <span className="text-bone-white font-medium">{email}</span>.
              Click the link to verify your account and access your merchant dashboard.
            </p>
            <div className="border-t border-navy-700/30 pt-6">
              <p className="font-mono text-[10px] text-bone-white/30 uppercase tracking-widest">
                Didn't receive it? Check your spam folder.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-950 flex">
      <div className="hidden lg:flex lg:w-[45%] flex-col justify-between p-12 xl:p-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-navy-900/50 to-navy-950" />
        <div className="relative z-10">
          <Link to="/" className="font-mono text-bone-white tracking-widest text-sm font-medium uppercase">
            BONESPAY
          </Link>
        </div>

        <div className="relative z-10 flex-1 flex flex-col justify-center max-w-md">
          <h1 className="font-serif text-bone-white text-3xl xl:text-4xl leading-tight mb-6">
            Accept card payments.
            <br />
            <span className="text-navy-200">Settle in USDC.</span>
          </h1>
          <p className="font-sans text-navy-200/60 text-sm leading-relaxed mb-10">
            Join 1,200+ merchants who process payments through BonesPay.
            No approval process. No fund holds. No rolling reserves.
          </p>

          <div className="space-y-4">
            {FEATURES.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-subtle bg-navy-800/60 flex items-center justify-center flex-shrink-0">
                  <Icon size={14} className="text-bone-white/60" strokeWidth={1.5} />
                </div>
                <span className="font-sans text-sm text-bone-white/70">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <div className="border-t border-navy-700/30 pt-6">
            <p className="font-mono text-[10px] text-bone-white/20 uppercase tracking-widest">
              The blockchain never forgets.
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10">
            <Link to="/" className="font-mono text-bone-white tracking-widest text-sm font-medium uppercase">
              BONESPAY
            </Link>
          </div>

          <div className="mb-8">
            <h2 className="font-serif text-bone-white text-2xl md:text-3xl mb-2">
              Create your account
            </h2>
            <p className="font-sans text-navy-200/50 text-sm">
              Start accepting payments in minutes
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="border border-red-500/30 bg-red-500/10 rounded-subtle px-4 py-3">
                <p className="font-sans text-sm text-red-300">{error}</p>
              </div>
            )}

            <div>
              <label className="block font-mono text-[10px] text-bone-white/40 uppercase tracking-widest mb-2">
                Business Name
              </label>
              <input
                type="text"
                required
                placeholder="Acme Inc."
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                className="w-full border border-navy-700/40 rounded-subtle px-4 py-3 bg-navy-900/30 text-bone-white font-sans text-sm focus:outline-none focus:border-navy-500 transition-colors placeholder:text-bone-white/20"
              />
            </div>

            <div>
              <label className="block font-mono text-[10px] text-bone-white/40 uppercase tracking-widest mb-2">
                Email
              </label>
              <input
                type="email"
                required
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-navy-700/40 rounded-subtle px-4 py-3 bg-navy-900/30 text-bone-white font-sans text-sm focus:outline-none focus:border-navy-500 transition-colors placeholder:text-bone-white/20"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block font-mono text-[10px] text-bone-white/40 uppercase tracking-widest mb-2">
                  Password
                </label>
                <input
                  type="password"
                  required
                  minLength={8}
                  placeholder="Min. 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-navy-700/40 rounded-subtle px-4 py-3 bg-navy-900/30 text-bone-white font-sans text-sm focus:outline-none focus:border-navy-500 transition-colors placeholder:text-bone-white/20"
                />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="font-mono text-[10px] text-bone-white/40 uppercase tracking-widest">
                    Confirm Password
                  </label>
                  {password.length > 0 && confirmPassword.length > 0 && password.length === confirmPassword.length && password === confirmPassword && (
                    <CheckCircle size={14} className="text-emerald-400" strokeWidth={2} />
                  )}
                </div>
                <input
                  type="password"
                  required
                  minLength={8}
                  placeholder="Re-enter password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full border border-navy-700/40 rounded-subtle px-4 py-3 bg-navy-900/30 text-bone-white font-sans text-sm focus:outline-none focus:border-navy-500 transition-colors placeholder:text-bone-white/20"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-bone-white text-navy-900 font-mono text-sm font-medium py-3.5 rounded-subtle hover:bg-navy-100 transition-colors disabled:opacity-50 uppercase tracking-wider flex items-center justify-center gap-2 group mt-2"
            >
              {loading ? (
                'Creating account...'
              ) : (
                <>
                  Create Account
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          <p className="mt-8 text-center font-sans text-sm text-bone-white/30">
            Already have an account?{' '}
            <Link to="/login" className="text-bone-white/60 hover:text-bone-white transition-colors">
              Sign in
            </Link>
          </p>

          <div className="mt-10 border-t border-navy-700/20 pt-6">
            <p className="font-mono text-[9px] text-bone-white/15 uppercase tracking-widest text-center leading-relaxed">
              By creating an account you agree to our{' '}
              <Link to="/terms" className="text-bone-white/30 hover:text-bone-white/50 transition-colors">Terms</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-bone-white/30 hover:text-bone-white/50 transition-colors">Privacy Policy</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
